import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, Alert, CircularProgress, Snackbar, Button } from '@mui/material';
import { IconRefresh, IconReload } from '@tabler/icons-react';
import type {
  ReconciliationDashboard,
  ReconciliationVariance,
  ReconciliationException,
  ReconciliationStatus,
  VarianceType,
} from '@techgitter/contracts';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import {
  fetchReconciliationDashboard,
  fetchVariances,
  fetchExceptions,
  resolveVariance,
  resolveException,
} from '../services/reconciliationApi';
import { ResolveDialog, ResolveRequest } from '../components/reconciliation/ResolveDialog';
import { DeferDialog } from '../components/reconciliation/DeferDialog';
import { useRerunReconcile } from '../components/reconciliation/useRerunReconcile';
import { WorklistPanel } from '../components/reconciliation/WorklistPanel';
import { InactivePanel } from '../components/reconciliation/InactivePanel';
import { TrialsPanel } from '../components/reconciliation/TrialsPanel';
import { RenewalRowsPanel } from '../components/reconciliation/RenewalRowsPanel';
import { MappingsPanel } from '../components/reconciliation/MappingsPanel';
import { DeferredPanel } from '../components/reconciliation/DeferredPanel';
import { CustomerDrillDownDialog } from '../components/reconciliation/CustomerDrillDownDialog';
import { CreateSubscriptionAndBillPanel } from '../components/subscriptions/CreateSubscriptionAndBillPanel';
import { updateVendorSubscription } from '../services/vendorSubscriptionService';
import { TERM_MISMATCH_TOOLTIP } from '../components/reconciliation/termMismatch';
import {
  SecondaryToolbar,
  ToolbarChip,
  ToolbarSelect,
  ToolbarSpacer,
  ToolbarButton,
} from '../components/toolbar';
import { textMatches } from '../components/reconciliation/reconciliationFilter';
import { ReconciliationFilterFields } from '../components/reconciliation/ReconciliationFilterFields';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip } from '../components/chip';
import { CARD_SX, OUTLINED_BTN_SX, ACCENT_LINK_SX } from '../styles/designSystem';

type StatusFilter = ReconciliationStatus | 'all';
type TabKey = 'variances' | 'exceptions' | 'worklist' | 'inactive' | 'trials' | 'renewals' | 'mappings' | 'deferred';
// The forward-looking Control 3 (Renewal) variance types; 'renewal' is a
// client-side rollup filter (the backend filters by a single type).
const RENEWAL_TYPES: VarianceType[] = ['renewalSeat', 'renewalTerm', 'renewalPlan'];

/**
 * What a variance's `actualValue` slot holds, per type. `expectedValue` is the
 * commitment for every type; the "actual" side is whichever source the control
 * compared it against — and for a **coverage** break that is what the invoice
 * schedule bills, NOT what the vendor reported. Labelling it "vendor reported"
 * sent an operator to check the Synnex file for a number that was ours.
 */
const ACTUAL_SOURCE_LABEL: Record<VarianceType, string> = {
  seat: 'Vendor reported',
  term: 'Vendor reported',
  coverage: 'Billed',
  renewalSeat: 'Scheduled',
  renewalTerm: 'Scheduled',
  renewalPlan: 'Scheduled',
};
const ACTUAL_SOURCE_HINT: Record<VarianceType, string> = {
  seat: 'Seats on the vendor\'s latest file',
  term: 'Renewal on the vendor\'s latest file',
  coverage: 'Seats the invoice schedule bills the customer for',
  renewalSeat: 'Seats on the vendor\'s scheduled renewal',
  renewalTerm: 'Term on the vendor\'s scheduled renewal',
  renewalPlan: 'Plan on the vendor\'s scheduled renewal',
};
type TypeFilter = VarianceType | 'all' | 'renewal';

interface CustomerDrillTarget {
  customerId: string;
  customerName?: string | null;
}

const TABS: { key: TabKey; label: string }[] = [
  { key: 'variances', label: 'Variances' },
  { key: 'exceptions', label: 'Exceptions' },
  { key: 'worklist', label: 'Reference Data' },
  { key: 'inactive', label: 'Inactive' },
  { key: 'trials', label: 'Trials' },
  { key: 'renewals', label: 'Renewals' },
  { key: 'deferred', label: 'Deferred' },
  { key: 'mappings', label: 'Mappings' },
];

const VARIANCE_TYPE_LABEL: Record<VarianceType, string> = {
  seat: 'Seat',
  term: 'Term',
  coverage: 'Coverage',
  renewalSeat: 'Renewal: Seats',
  renewalTerm: 'Renewal: Term',
  renewalPlan: 'Renewal: Plan',
};
const EXCEPTION_TYPE_LABEL: Record<string, string> = {
  unmatchedCustomer: 'Unmatched Customer',
  unmatchedProduct: 'Unmatched Product',
};
const RESOLUTION_TYPE_LABEL: Record<string, string> = {
  adjustVendorSubscription: 'Adjusted vendor sub',
  adjustCommercialSubscription: 'Adjusted commercial sub',
  externalVendorAction: 'External vendor action',
  acceptVendorAsCorrect: 'Accepted vendor',
  ignore: 'Ignored',
  createSubscription: 'Created subscription',
  defer: 'Deferred',
};

/** Row action — compact accent-outlined "Resolve" button. */
const RESOLVE_BTN_SX = { ...OUTLINED_BTN_SX, textTransform: 'none', fontSize: 12, py: 0.25, px: 1, minWidth: 0 };
/** Inline accent text link (customer drill-down). */
const LINK_BTN_SX = {
  ...ACCENT_LINK_SX,
  textTransform: 'none',
  p: 0,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 500,
  justifyContent: 'flex-start',
  '&:hover': { backgroundColor: 'transparent', textDecoration: 'underline' },
};

function fmtDate(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

/** Date-only (no time) — for forward-looking renewal dates where the time of
 *  day is noise. */
function fmtDateOnly(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

interface Tile {
  label: string;
  value: number;
  /** Left-stripe accent (hex). */
  color: string;
  onClick: () => void;
}

interface ResolveTarget {
  title: string;
  subtitle: string;
  /** Offer the optional Action Date (variances only — defers the break). */
  showActionDate?: boolean;
  /** Optional "fix the commitment" action surfaced in the resolve dialog. */
  applyAction?: { label: string; run: () => Promise<void> };
  run: (request: ResolveRequest) => Promise<void>;
}

export const ReconciliationPage: React.FC = () => {
  const currentUser = useCurrentUser();
  const canResolve = !!currentUser?.hasAnyRole('Admin');

  const [dashboard, setDashboard] = useState<ReconciliationDashboard | null>(null);
  const [variances, setVariances] = useState<ReconciliationVariance[]>([]);
  const [exceptions, setExceptions] = useState<ReconciliationException[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<TabKey>('variances');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('open');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  // Free-text Customer / Product filters — shared across every tab (applied
  // client-side by each panel to whichever customer/product fields it carries).
  const [customerFilter, setCustomerFilter] = useState('');
  const [productFilter, setProductFilter] = useState('');

  const [resolveTarget, setResolveTarget] = useState<ResolveTarget | null>(null);
  const [subBillTarget, setSubBillTarget] = useState<ReconciliationVariance | null>(null);
  const [deferTarget, setDeferTarget] = useState<ReconciliationVariance | null>(null);
  const [drillTarget, setDrillTarget] = useState<CustomerDrillTarget | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const statusParam = statusFilter === 'all' ? undefined : statusFilter;

  const loadDashboard = useCallback(async (signal?: AbortSignal) => {
    try {
      const tiles = await fetchReconciliationDashboard(signal);
      if (!signal?.aborted) setDashboard(tiles);
    } catch (e) {
      if (e instanceof Error && e.name === 'AbortError') return;
      // Tiles are non-critical; surface via the page error only if nothing loaded.
    }
  }, []);

  const loadLists = useCallback(
    async (signal?: AbortSignal) => {
      if (
        tab === 'worklist' || tab === 'inactive' || tab === 'trials' ||
        tab === 'renewals' || tab === 'mappings' || tab === 'deferred'
      ) {
        // These tabs are self-contained (their own fetches).
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        if (tab === 'variances') {
          // 'renewal' is a client-side rollup of the three renewal* types (the
          // backend filters by a single type), so fetch all and filter here.
          const backendType =
            typeFilter === 'all' || typeFilter === 'renewal' ? undefined : typeFilter;
          const items = await fetchVariances(
            { status: statusParam, type: backendType },
            signal,
          );
          const filtered =
            typeFilter === 'renewal'
              ? items.filter((v) => RENEWAL_TYPES.includes(v.varianceType))
              : items;
          if (!signal?.aborted) setVariances(filtered);
        } else {
          const items = await fetchExceptions({ status: statusParam }, signal);
          if (!signal?.aborted) setExceptions(items);
        }
      } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') return;
        setError(e instanceof Error ? e.message : 'Failed to load');
      } finally {
        if (!signal?.aborted) setLoading(false);
      }
    },
    [tab, statusParam, typeFilter],
  );

  useEffect(() => {
    const controller = new AbortController();
    loadDashboard(controller.signal);
    return () => controller.abort();
  }, [loadDashboard]);

  useEffect(() => {
    const controller = new AbortController();
    loadLists(controller.signal);
    return () => controller.abort();
  }, [loadLists]);

  const refreshAll = useCallback(() => {
    loadDashboard();
    loadLists();
  }, [loadDashboard, loadLists]);

  // Re-run reconcile (Control 1 + Control 2) over the existing latest batch — no
  // Synnex re-pull. Clears breaks whose underlying data has been fixed.
  const { rerun, running: rerunning } = useRerunReconcile();
  const handleRerun = useCallback(async () => {
    try {
      const res = await rerun();
      setToast(
        res.running
          ? 'Reconcile started — still running; refreshing shortly.'
          : `Reconcile complete — ${res.processed} batch(es) processed.`,
      );
      window.setTimeout(refreshAll, res.running ? 30_000 : 0);
    } catch (e) {
      setToast(e instanceof Error ? e.message : 'Failed to re-run reconcile');
    }
  }, [rerun, refreshAll]);

  const focusVariances = (type: TypeFilter) => {
    setTab('variances');
    setStatusFilter('open');
    setTypeFilter(type);
  };

  const tiles: Tile[] = useMemo(
    () => [
      {
        label: 'Open Seat Variances',
        value: dashboard?.openSeatVariances ?? 0,
        color: '#4A90D9',
        onClick: () => focusVariances('seat'),
      },
      {
        label: 'Open Term Variances',
        value: dashboard?.openTermVariances ?? 0,
        color: '#9B59B6',
        onClick: () => focusVariances('term'),
      },
      {
        label: 'Open Coverage Variances',
        value: dashboard?.openCoverageVariances ?? 0,
        color: '#16A085',
        onClick: () => focusVariances('coverage'),
      },
      {
        label: 'Open Renewal Variances',
        value: dashboard?.openRenewalVariances ?? 0,
        color: '#E67E22',
        onClick: () => focusVariances('renewal'),
      },
      {
        label: 'Open Exceptions',
        value: dashboard?.openExceptions ?? 0,
        color: '#EF9F27',
        onClick: () => {
          setTab('exceptions');
          setStatusFilter('open');
        },
      },
    ],
    [dashboard],
  );

  // Apply the shared Customer / Product filters to the two inline lists. A
  // variance carries the customer and vendor-product on the row; an exception
  // only has the raw unmatched identifier in `detail`, so both filters match
  // against that.
  const filteredVariances = useMemo(
    () =>
      variances.filter(
        (v) =>
          textMatches(customerFilter, v.customerName, v.customerId) &&
          textMatches(productFilter, v.vendorProductName, v.vendorProductSku),
      ),
    [variances, customerFilter, productFilter],
  );
  const filteredExceptions = useMemo(
    () =>
      exceptions.filter(
        (x) =>
          textMatches(customerFilter, x.detail) && textMatches(productFilter, x.detail),
      ),
    [exceptions, customerFilter, productFilter],
  );

  /** No commitment on file (coverage, or a seat break the vendor reports with no
   *  subscription) → the usual fix is to create the subscription and bill it,
   *  which the row offers as a separate "Create sub" action. It is deliberately
   *  NOT the only exit: a line the vendor has since dropped needs closing, not
   *  creating, so plain Resolve always opens the resolution-type dialog. */
  const canCreateSubscription = (v: ReconciliationVariance) =>
    !v.subscriptionId && !!v.customerId && !!v.vendorProductId;

  const openVarianceResolve = (v: ReconciliationVariance) => {
    // Seat break with a matched subscription → offer to bump the commitment to
    // the reported seat count as part of resolving. For a Seat variance that's
    // the vendor-reported seats; for a RenewalSeat it's the *scheduled* seats —
    // adjusting the commitment ahead of the renewal (the Control 3 resolution).
    // In both cases actualValue holds the seat count to apply.
    const vendorSeats = Number(v.actualValue);
    const canApply =
      (v.varianceType === 'seat' || v.varianceType === 'renewalSeat') &&
      !!v.subscriptionId &&
      v.actualValue != null &&
      Number.isFinite(vendorSeats);
    setResolveTarget({
      title: `Resolve ${VARIANCE_TYPE_LABEL[v.varianceType]} variance`,
      subtitle: `${v.customerName ?? v.customerId ?? 'Customer'} · ${v.vendorProductName ?? v.vendorProductSku ?? ''} — committed ${v.expectedValue ?? '—'}, ${ACTUAL_SOURCE_LABEL[v.varianceType].toLowerCase()} ${v.actualValue ?? '—'}`,
      showActionDate: true,
      applyAction: canApply
        ? {
            label: `set commitment seats to ${vendorSeats}`,
            run: async () => {
              await updateVendorSubscription(v.subscriptionId!, { seats: vendorSeats });
            },
          }
        : undefined,
      run: async (req) => {
        await resolveVariance(v.id, req);
      },
    });
  };

  // Defer a term break pending a monthly→annual vendor conversion — resolves as
  // 'defer' with a suggested action date (annual invoice next-run + buffer).
  const openVarianceDefer = (v: ReconciliationVariance) => {
    setDeferTarget(v);
  };

  const openExceptionResolve = (x: ReconciliationException) => {
    setResolveTarget({
      title: `Resolve ${EXCEPTION_TYPE_LABEL[x.exceptionType] ?? x.exceptionType}`,
      subtitle: x.detail ?? '',
      run: async (req) => {
        await resolveException(x.id, req);
      },
    });
  };

  return (
    <>
      {/* Header + KPI tiles (the sidebar already names the page, so no h1) */}
      <Box sx={{ px: 2, pt: '10px' }}>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 1.5, maxWidth: 720 }}>
          Supplier subscriptions vs. internal records and billing. A finance control surface —
          every variance is investigated and resolved with a recorded outcome.
          {!canResolve && ' Read-only — Admin role required to resolve.'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {tiles.map((tile) => (
            <Box
              key={tile.label}
              onClick={tile.onClick}
              sx={[
                CARD_SX,
                {
                  p: '12px 16px',
                  flex: '1 1 180px',
                  minWidth: 160,
                  cursor: 'pointer',
                  borderLeft: '3px solid',
                  borderLeftColor: tile.color,
                  transition: 'box-shadow 0.12s ease',
                  '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.12), 0 0 0 0.5px rgba(0,0,0,0.08)' },
                },
              ]}
            >
              <Typography sx={{ fontSize: 28, fontWeight: 600, color: '#1a1a1a', lineHeight: 1.1 }}>
                {tile.value}
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 500,
                  color: '#888',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  mt: 0.5,
                }}
              >
                {tile.label}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* View switcher */}
      <SecondaryToolbar floating>
        {TABS.map((t) => (
          <ToolbarChip
            key={t.key}
            label={t.label}
            active={tab === t.key}
            onClick={() => setTab(t.key)}
          />
        ))}
      </SecondaryToolbar>

      {tab === 'deferred' ? (
        <DeferredPanel
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={setCustomerFilter}
          onProductFilterChange={setProductFilter}
          canResolve={canResolve}
        />
      ) : tab === 'mappings' ? (
        <MappingsPanel
          canEdit={canResolve}
          productFilter={productFilter}
          onProductFilterChange={setProductFilter}
        />
      ) : tab === 'inactive' ? (
        <InactivePanel
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={setCustomerFilter}
          onProductFilterChange={setProductFilter}
        />
      ) : tab === 'trials' ? (
        <TrialsPanel
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={setCustomerFilter}
          onProductFilterChange={setProductFilter}
        />
      ) : tab === 'renewals' ? (
        <RenewalRowsPanel
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={setCustomerFilter}
          onProductFilterChange={setProductFilter}
        />
      ) : tab === 'worklist' ? (
        <WorklistPanel
          canCreate={canResolve}
          customerFilter={customerFilter}
          productFilter={productFilter}
          onCustomerFilterChange={setCustomerFilter}
          onProductFilterChange={setProductFilter}
        />
      ) : (
        <>
          {/* Filters */}
          <SecondaryToolbar floating>
            <ToolbarSelect
              label="Status"
              value={statusFilter}
              onChange={(v) => setStatusFilter(v as StatusFilter)}
              options={[
                { value: 'open', label: 'Open' },
                { value: 'resolved', label: 'Resolved' },
                { value: 'all', label: 'All' },
              ]}
            />
            {tab === 'variances' && (
              <ToolbarSelect
                label="Type"
                value={typeFilter}
                onChange={(v) => setTypeFilter(v as TypeFilter)}
                options={[
                  { value: 'all', label: 'All types' },
                  { value: 'seat', label: 'Seat' },
                  { value: 'term', label: 'Term' },
                  { value: 'coverage', label: 'Coverage' },
                  { value: 'renewal', label: 'All renewals' },
                  { value: 'renewalSeat', label: 'Renewal: Seats' },
                  { value: 'renewalTerm', label: 'Renewal: Term' },
                  { value: 'renewalPlan', label: 'Renewal: Plan' },
                ]}
              />
            )}
            <ReconciliationFilterFields
              customerFilter={customerFilter}
              productFilter={productFilter}
              onCustomerFilterChange={setCustomerFilter}
              onProductFilterChange={setProductFilter}
            />
            <ToolbarSpacer />
            {canResolve && (
              <ToolbarButton
                icon={IconReload}
                onClick={handleRerun}
                disabled={rerunning}
                title="Re-run reconcile over the existing Synnex batch (Control 1 + coverage) — no re-pull"
              >
                {rerunning ? 'Reconciling…' : 'Re-run reconcile'}
              </ToolbarButton>
            )}
            <ToolbarButton icon={IconRefresh} onClick={refreshAll}>
              Refresh
            </ToolbarButton>
          </SecondaryToolbar>

          <Box sx={{ p: '12px 16px 24px' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {error}
              </Alert>
            )}

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} />
              </Box>
            ) : tab === 'variances' ? (
              <VariancesTable
                rows={filteredVariances}
                canResolve={canResolve}
                onResolve={openVarianceResolve}
                canCreateSubscription={canCreateSubscription}
                onCreateSubscription={setSubBillTarget}
                onDefer={openVarianceDefer}
                onCustomerClick={(customerId, customerName) =>
                  setDrillTarget({ customerId, customerName })
                }
              />
            ) : (
              <ExceptionsTable
                rows={filteredExceptions}
                canResolve={canResolve}
                onResolve={openExceptionResolve}
              />
            )}
          </Box>
        </>
      )}

      {drillTarget && (
        <CustomerDrillDownDialog
          open
          customerId={drillTarget.customerId}
          customerName={drillTarget.customerName}
          canEdit={canResolve}
          onClose={() => setDrillTarget(null)}
        />
      )}

      {subBillTarget && (
        <CreateSubscriptionAndBillPanel
          open
          customer={{
            dataverseId: subBillTarget.customerId!,
            name: subBillTarget.customerName ?? undefined,
          }}
          vendorProduct={{
            id: subBillTarget.vendorProductId!,
            name: subBillTarget.vendorProductName ?? undefined,
            sku: subBillTarget.vendorProductSku ?? undefined,
            vendorId: subBillTarget.vendorId ?? '',
            billingTerm: subBillTarget.vendorProductBillingTerm ?? null,
          }}
          defaultSeats={Number(subBillTarget.actualValue) || 1}
          onClose={() => setSubBillTarget(null)}
          onCompleted={async (result) => {
            const v = subBillTarget;
            setSubBillTarget(null);
            try {
              await resolveVariance(v.id, {
                resolutionType: 'createSubscription',
                resolutionNotes:
                  result.billing === 'failed'
                    ? 'Created subscription; billing line must be added manually.'
                    : 'Created subscription and added it to the customer’s recurring schedule.',
              });
              setToast('Resolved');
              refreshAll();
            } catch (e) {
              setToast(e instanceof Error ? e.message : 'Failed to record resolution');
            }
          }}
        />
      )}

      {resolveTarget && (
        <ResolveDialog
          open
          title={resolveTarget.title}
          subtitle={resolveTarget.subtitle}
          showActionDate={resolveTarget.showActionDate}
          applyAction={resolveTarget.applyAction}
          onResolve={resolveTarget.run}
          onClose={() => setResolveTarget(null)}
          onResolved={() => {
            setResolveTarget(null);
            setToast('Resolved');
            refreshAll();
          }}
        />
      )}

      <DeferDialog
        open={!!deferTarget}
        variance={deferTarget}
        onClose={() => setDeferTarget(null)}
        onDeferred={() => {
          setDeferTarget(null);
          setToast('Deferred');
          refreshAll();
        }}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </>
  );
};

const VariancesTable: React.FC<{
  rows: ReconciliationVariance[];
  canResolve: boolean;
  onResolve: (v: ReconciliationVariance) => void;
  onDefer: (v: ReconciliationVariance) => void;
  /** Whether the row should offer the create-subscription-and-bill action. */
  canCreateSubscription: (v: ReconciliationVariance) => boolean;
  onCreateSubscription: (v: ReconciliationVariance) => void;
  onCustomerClick: (customerId: string, customerName?: string | null) => void;
}> = ({
  rows,
  canResolve,
  onResolve,
  onDefer,
  canCreateSubscription,
  onCreateSubscription,
  onCustomerClick,
}) => {
  const colSpan = canResolve ? 9 : 8;
  const actualLabels = new Set(rows.map((v) => ACTUAL_SOURCE_LABEL[v.varianceType]));
  const actualHeader = actualLabels.size === 1 ? [...actualLabels][0] : 'Actual';
  return (
    <DataTableCard>
      <DataTable>
        <thead>
          <tr>
            <DataTableHeadCell>Type</DataTableHeadCell>
            <DataTableHeadCell>Status</DataTableHeadCell>
            <DataTableHeadCell>Customer</DataTableHeadCell>
            <DataTableHeadCell>Vendor</DataTableHeadCell>
            <DataTableHeadCell>Product</DataTableHeadCell>
            <DataTableHeadCell align="right">Committed</DataTableHeadCell>
            {/* The "actual" side differs by type (vendor file / invoice schedule /
                scheduled renewal). Name it when the rows agree; otherwise each
                cell carries its own label. */}
            <DataTableHeadCell align="right">{actualHeader}</DataTableHeadCell>
            <DataTableHeadCell>Detected</DataTableHeadCell>
            {canResolve && <DataTableHeadCell align="right">Action</DataTableHeadCell>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <DataTableEmpty colSpan={colSpan}>
              No variances for the selected filters.
            </DataTableEmpty>
          ) : (
            rows.map((v) => (
              <DataTableRow key={v.id}>
                <DataTableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip>{VARIANCE_TYPE_LABEL[v.varianceType]}</Chip>
                    {v.termMismatch && (
                      <Chip tone="warning" title={TERM_MISMATCH_TOOLTIP}>
                        Term mismatch
                      </Chip>
                    )}
                    {/* Suppress the amber "act soon" chip when the row self-resolves
                        or ends on renewal — those carry their own status + date and
                        the urgency contradicts them. */}
                    {v.imminent && !v.resolvesOnRenewal && !v.endsOnRenewal && (
                      <Chip tone="warning" title="Renews within the imminence window — act soon">
                        imminent
                      </Chip>
                    )}
                    {v.resolvesOnRenewal && (
                      <Chip
                        tone="success"
                        title="The scheduled renewal brings the vendor state back to the commitment — this variance self-corrects on renewal (no action needed)"
                      >
                        Resolves on renewal
                      </Chip>
                    )}
                    {v.endsOnRenewal && (
                      <Chip
                        tone="info"
                        title="The subscription is expiring at its next term — this over/under-count disappears when it ends"
                      >
                        Ends on renewal
                      </Chip>
                    )}
                  </Box>
                  {v.effectiveDate && (
                    <Typography sx={{ fontSize: 11, color: '#888', mt: 0.25 }}>
                      {v.endsOnRenewal ? 'ends' : 'renews'} {fmtDateOnly(v.effectiveDate)}
                      {typeof v.daysToRenewal === 'number'
                        ? ` · ${v.daysToRenewal >= 0 ? `${v.daysToRenewal}d` : 'overdue'}`
                        : ''}
                    </Typography>
                  )}
                </DataTableCell>
                <DataTableCell>
                  <Chip tone={v.status === 'open' ? 'warning' : 'success'}>{v.status}</Chip>
                </DataTableCell>
                <DataTableCell>
                  {v.customerId ? (
                    <Button
                      variant="text"
                      size="small"
                      sx={LINK_BTN_SX}
                      onClick={() => onCustomerClick(v.customerId!, v.customerName)}
                    >
                      {v.customerName ?? v.customerId}
                    </Button>
                  ) : (
                    '—'
                  )}
                </DataTableCell>
                <DataTableCell>{v.vendorName ?? '—'}</DataTableCell>
                <DataTableCell>{v.vendorProductName ?? v.vendorProductSku ?? '—'}</DataTableCell>
                <DataTableCell align="right">{v.expectedValue ?? '—'}</DataTableCell>
                <DataTableCell align="right">
                  <span title={ACTUAL_SOURCE_HINT[v.varianceType]}>
                    {v.actualValue ?? '—'}
                    {actualHeader === 'Actual' && (
                      <Typography component="span" variant="caption" sx={{ color: '#888', ml: 0.5 }}>
                        {ACTUAL_SOURCE_LABEL[v.varianceType].toLowerCase()}
                      </Typography>
                    )}
                  </span>
                </DataTableCell>
                <DataTableCell muted>{fmtDate(v.detectedAt)}</DataTableCell>
                {canResolve && (
                  <DataTableCell align="right">
                    {v.status === 'open' ? (
                      <Box sx={{ display: 'inline-flex', gap: 0.5, justifyContent: 'flex-end' }}>
                        {/* Term + coverage breaks can be deferred until an action
                            date (e.g. a monthly→annual vendor conversion, or a
                            billing fix). */}
                        {(v.varianceType === 'term' || v.varianceType === 'coverage') && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={RESOLVE_BTN_SX}
                            onClick={() => onDefer(v)}
                          >
                            Defer
                          </Button>
                        )}
                        {/* No commitment on file: creating one (and billing it)
                            is the usual fix, but Resolve stays available so a
                            line the vendor has since dropped can be closed. */}
                        {canCreateSubscription(v) && (
                          <Button
                            size="small"
                            variant="outlined"
                            sx={RESOLVE_BTN_SX}
                            title="Create the subscription and add it to the customer's recurring schedule"
                            onClick={() => onCreateSubscription(v)}
                          >
                            Create sub
                          </Button>
                        )}
                        <Button size="small" variant="outlined" sx={RESOLVE_BTN_SX} onClick={() => onResolve(v)}>
                          Resolve
                        </Button>
                      </Box>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ color: '#888' }}
                        title={v.resolutionNotes ?? ''}
                      >
                        {(v.resolutionType && RESOLUTION_TYPE_LABEL[v.resolutionType]) ??
                          v.resolutionType ??
                          'resolved'}
                      </Typography>
                    )}
                  </DataTableCell>
                )}
              </DataTableRow>
            ))
          )}
        </tbody>
      </DataTable>
    </DataTableCard>
  );
};

const ExceptionsTable: React.FC<{
  rows: ReconciliationException[];
  canResolve: boolean;
  onResolve: (x: ReconciliationException) => void;
}> = ({ rows, canResolve, onResolve }) => {
  const colSpan = canResolve ? 5 : 4;
  return (
    <DataTableCard>
      <DataTable>
        <thead>
          <tr>
            <DataTableHeadCell>Type</DataTableHeadCell>
            <DataTableHeadCell>Status</DataTableHeadCell>
            <DataTableHeadCell>Vendor</DataTableHeadCell>
            <DataTableHeadCell>Detail</DataTableHeadCell>
            {canResolve && <DataTableHeadCell align="right">Action</DataTableHeadCell>}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <DataTableEmpty colSpan={colSpan}>
              No exceptions for the selected filters.
            </DataTableEmpty>
          ) : (
            rows.map((x) => (
              <DataTableRow key={x.id}>
                <DataTableCell>{EXCEPTION_TYPE_LABEL[x.exceptionType] ?? x.exceptionType}</DataTableCell>
                <DataTableCell>
                  <Chip tone={x.status === 'open' ? 'warning' : 'success'}>{x.status}</Chip>
                </DataTableCell>
                <DataTableCell>{x.vendorName ?? '—'}</DataTableCell>
                <DataTableCell>{x.detail ?? '—'}</DataTableCell>
                {canResolve && (
                  <DataTableCell align="right">
                    {x.status === 'open' ? (
                      <Button size="small" variant="outlined" sx={RESOLVE_BTN_SX} onClick={() => onResolve(x)}>
                        Resolve
                      </Button>
                    ) : (
                      <Typography
                        variant="caption"
                        sx={{ color: '#888' }}
                        title={x.resolutionNotes ?? ''}
                      >
                        {x.resolutionType ?? 'resolved'}
                      </Typography>
                    )}
                  </DataTableCell>
                )}
              </DataTableRow>
            ))
          )}
        </tbody>
      </DataTable>
    </DataTableCard>
  );
};

export default ReconciliationPage;
